import { panelHeaderControlClass } from "./SplitPanelHeader.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { cn } from "@/lib/utils.ts";
import { EllipsisVertical } from "lucide-react";
import type React from "react";
import { type RefObject, useLayoutEffect, useState } from "react";
import { BAR_TIER_CLASS, type BarTier, isInBar } from "./headerActionTiers.ts";

/**
 * One control a panel header can offer, in either of the two places it can
 * appear. The same record drives the bar button and the menu item, so an action
 * cannot look or report differently depending on how wide the column happens to
 * be.
 */
export type PanelHeaderAction = {
  key: string;
  icon: React.FC<{ className?: string }>;
  label: string;
  onClick: () => void;
  /**
   * Carried by both the bar button and the menu item, so an action keeps
   * reporting under one name wherever the width happens to put it. Only one of
   * the two is ever clickable, so the count stays honest.
   */
  statsigName: string;
  tier: BarTier;
  disabled?: boolean;
  /** Removes the action everywhere, bar and menu alike — it gates the action
   *  itself, not one of its renderings. */
  visible?: boolean;
  badge?: boolean;
  /**
   * Extra classes for the glyph, carried by the bar button and the menu item
   * alike so an action looks the same wherever the width happens to put it.
   */
  iconClassName?: string;
};

/**
 * The width the tiers are measured against, as a number rather than as a
 * container query.
 *
 * CSS can hide a control at a width, but it cannot tell the menu how many
 * things it is about to hold — and that count is what decides whether the menu
 * earns its click or hands its one item back to the bar. A query would also
 * miss the menu's own contents, which Radix portals outside the header and so
 * outside any `@container` the header establishes.
 *
 * Layout effect, and measured directly rather than waiting to be told: a
 * `ResizeObserver` delivers its first callback on a rendering step, so between
 * mount and that step the width would read 0 and the menu would briefly claim
 * every control the bar is already showing.
 *
 * `ref` must point at an element that stays mounted for the panel's life — see
 * {@link PanelHeaderMenu}, which is why its trigger hides rather than unmounts.
 */
export const usePanelHeaderWidth = (ref: RefObject<HTMLElement | null>) => {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const header = ref.current?.closest("header");
    if (!header) return;

    setWidth(header.clientWidth);
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(header);
    return () => observer.disconnect();
  }, [ref]);

  return width;
};

/**
 * Splits a panel header's actions between the bar and its overflow menu, at the
 * width the header actually has.
 *
 * A pure function taking the width rather than measuring it, because a caller
 * usually has its own width-dependent menu items to count — a contacts row, a
 * debug submenu — and those have to be decided before `extraMenuItems` can be
 * passed. Measuring here would make that circular. Pair with
 * {@link usePanelHeaderWidth}.
 *
 * The caller keeps ownership of what the menu *contains* — order, separators,
 * and any item that has no bar form — because those differ per panel. What is
 * shared is the arithmetic every panel header would otherwise re-derive: which
 * controls the bar is showing, which the menu must therefore hold, and whether
 * a menu is worth offering at all.
 *
 * `extraMenuItems` counts items the caller renders into the menu itself, with
 * no entry in `actions` — a contacts row, a debug submenu. They cannot be
 * promoted into the bar (they have no bar form), but they do count toward
 * whether the menu earns its click.
 */
export const splitPanelHeaderActions = ({
  actions: allActions,
  headerWidth = 0,
  extraMenuItems = 0,
}: {
  actions: PanelHeaderAction[];
  /**
   * Omit where no action has a width-dependent tier. A panel whose actions are
   * all `never` gets the same split at every width, so measuring buys nothing
   * and costs a `ResizeObserver` that re-renders the panel on each frame of a
   * divider drag. Supply it as soon as one action earns a place in the bar.
   */
  headerWidth?: number;
  extraMenuItems?: number;
}) => {
  const actions = allActions.filter((a) => a.visible !== false);

  // The menu holds exactly what the bar is not currently showing, so no control
  // is offered twice.
  const menuActions = actions.filter((a) => !isInBar(a.tier, headerWidth));

  // A menu is only worth its click when it holds more than one thing: at one,
  // the item goes straight in the bar and the trigger disappears with it; at
  // zero, the trigger disappears alone.
  const menuItemCount = menuActions.length + extraMenuItems;
  const soleAction = menuItemCount === 1 && menuActions.length === 1 ? menuActions[0] : null;
  const showMenu = menuItemCount > 1;

  // A `never` action has no bar rendering at all, and a promoted `soleAction` is
  // rendered on its own by the caller — mapping either here would mount a
  // permanently hidden tooltip and put a second copy of its `data-statsig-name`
  // in the DOM.
  const barActions = actions.filter((a) => a.tier !== "never" && a.key !== soleAction?.key);

  return {
    /** Controls the bar renders, each hidden below its own tier. */
    barActions,
    /** Controls the bar is not showing, for the caller to order and render. */
    menuActions,
    /** The one menu item promoted back into the bar, or null. */
    soleAction,
    showMenu,
    /** What the menu would hold, `extraMenuItems` included — so a caller whose
     *  only menu item is one of its own can promote it on the same rule. */
    menuItemCount,
  };
};

/**
 * The same action as a menu row: icon, label, and the one `data-statsig-name`
 * it carries in either place.
 *
 * The bar half of this pair shipped first, with one caller writing the menu half
 * inline. Three callers write it now, and they had already drifted — one of the
 * copies dropped `disabled`, so an action that was supposed to be unavailable
 * rendered clickable. A record that claims to drive both renderings has to ship
 * both.
 */
export const PanelHeaderMenuItem = ({
  action: { icon: Icon, label, onClick, disabled, statsigName, iconClassName },
}: {
  action: PanelHeaderAction;
}) => (
  <DropdownMenuItem onClick={onClick} disabled={disabled} data-statsig-name={statsigName}>
    <Icon className={cn("size-4", iconClassName)} />
    {label}
  </DropdownMenuItem>
);

/** One action as a bar control: a naked icon button under a tooltip, hidden
 *  below its tier. */
export const PanelHeaderActionButton = ({
  action: { icon: Icon, label, onClick, disabled, statsigName, badge, iconClassName, tier },
  /** Omit for a promoted sole action: at that width the menu is its only
   *  alternative, and there isn't one. */
  hideBelowTier = true,
  ref,
}: {
  action: PanelHeaderAction;
  hideBelowTier?: boolean;
  /** For a bar control that opens an overlay, so focus has somewhere to return
   *  to. The menu's own items unmount with the menu, which is why only this
   *  half of the pair takes one. */
  ref?: React.Ref<HTMLButtonElement>;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        ref={ref}
        variant="ghost"
        size="icon"
        className={cn("relative", panelHeaderControlClass, hideBelowTier && BAR_TIER_CLASS[tier])}
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        data-statsig-name={statsigName}
      >
        <Icon className={cn("size-5", iconClassName)} />
        {badge && (
          // `opacity-100` because the control rests at 60% and opacity inherits
          // down the subtree — an unseen-update dot that dims with its button is
          // the one child that must not.
          <span className="absolute right-1 top-1 size-2 rounded-full bg-primary opacity-100 ring-2 ring-background" />
        )}
      </Button>
    </TooltipTrigger>
    <TooltipContent>{label}</TooltipContent>
  </Tooltip>
);

/**
 * The overflow menu's shell: trigger, and a menu that opens onto the panel's
 * title before its actions.
 *
 * The title is here because the header's own copy truncates to one line, so the
 * menu is where a long one can actually be read.
 *
 * Hidden rather than unmounted when it has fewer than two things to hold. That
 * is a requirement on `triggerRef` rather than a property of this component: a
 * caller passes the same ref to {@link usePanelHeaderWidth}, which walks up from
 * it to find the header, so unmounting the trigger nulls the ref and pins the
 * measured width at zero — which drops every tiered control into the menu and
 * leaves it there.
 */
export const PanelHeaderMenu = ({
  title,
  label,
  show,
  triggerRef,
  statsigName,
  children,
  onPointerDown,
  onKeyDown,
  onCloseAutoFocus,
}: {
  /**
   * Text, never a node. The menu renders its own copy, so an element passed here
   * would be mounted a second time — and an element carrying a ref (a portal
   * target, say) would hand that ref to the copy and take it back on close.
   * Omit it where there is no title worth repeating.
   */
  title?: string;
  /** The trigger's accessible name. Required rather than defaulted: a screen
   *  reader meets this control with no surrounding context, so "Open actions
   *  menu" would leave it ambiguous on a screen showing several. */
  label: string;
  show: boolean;
  triggerRef: RefObject<HTMLButtonElement | null>;
  statsigName: string;
  children: React.ReactNode;
  onPointerDown?: () => void;
  onKeyDown?: () => void;
  onCloseAutoFocus?: (event: Event) => void;
}) => (
  // Non-modal: Radix's default hides the rest of the page from assistive tech,
  // locks its scroll, and stops its pointer events while a handful of items are
  // showing. A panel header is not a surface that takes the app over, and the
  // canvas card this opens inside says so in as many words — it keeps the
  // conversation beside it live on purpose.
  <DropdownMenu modal={false}>
    <DropdownMenuTrigger asChild>
      <Button
        ref={triggerRef}
        variant="ghost"
        size="icon"
        className={cn(panelHeaderControlClass, !show && "hidden")}
        data-statsig-name={statsigName}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
      >
        <EllipsisVertical className="size-5" />
        <span className="sr-only">{label}</span>
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent
      align="end"
      alignOffset={8}
      className="flex flex-col gap-2 min-w-48"
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      onCloseAutoFocus={onCloseAutoFocus}
    >
      {title && (
        <>
          <DropdownMenuLabel className="text-xs text-muted-foreground font-medium line-clamp-2 max-w-56 pb-0">
            {title}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
        </>
      )}
      {children}
    </DropdownMenuContent>
  </DropdownMenu>
);
